import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { List, Checkbox, Input, Dropdown, Label, Icon } from 'semantic-ui-react'
import moment from 'moment'
import debounce from 'debounce'
import { createEntity, deleteEntity, fetchEntities, updateEntity, upsertEntity } from 'actions/entities'
import { entityRoute } from 'route_helpers'
import getTodos from 'selectors/todos'
import { getDossiers } from 'selectors/dossiers'

const CLIENT_PICKER_WINDOW = 'todoClientPicker'

class TodoList extends React.Component {
  constructor(props) {
    super(props)
    this.state = { newTitle: '', pendingClient: null, pickerOpen: false }
    this.titleRef = React.createRef()
    this.debouncedSearch = debounce((term) => this.props.fetchClients(term), 300)
  }

  componentDidMount() {
    this.props.fetchTodos()
  }

  componentWillUnmount() {
    this.debouncedSearch.clear()
  }

  handleChange = (event, { value }) => this.setState({ newTitle: value })

  handleKeyDown = (event) => {
    // While the picker owns the keyboard, let it handle Enter (don't commit a half-typed task).
    if (this.state.pickerOpen) return
    if (event.key === 'Enter') this.commit()
  }

  // Defer so a click that opens the picker can flip pickerOpen first — otherwise blurring
  // into the picker would prematurely commit a task the user is still attaching a client to.
  handleBlur = () => {
    setTimeout(() => { if (!this.state.pickerOpen) this.commit() }, 200)
  }

  // Enter (or blur) commits a complete-title tssk, with its pending client; empty does nothing.
  commit = () => {
    const title = this.state.newTitle.trim()
    if (!title) return
    this.props.addTodo(title, this.state.pendingClient)
    this.setState({ newTitle: '', pendingClient: null })
  }

  clientOptions = () => (this.props.dossiers || []).map((dossier) => ({
    key: dossier.id,
    value: dossier.id,
    text: dossier.patient ? `${dossier.patient.firstName} ${dossier.patient.lastName}` : '—',
  }))

  openPicker = () => {
    this.props.fetchClients('')
    // Defer the open one tick so the click that triggered it finishes bubbling to document
    // first. Otherwise SUIR's freshly-mounted "close on outside click" handler catches that
    // same click (its target — our affordance — is already unmounted, so it reads as
    // "outside") and immediately closes the picker, which ate the arrow-key focus and let the
    // deferred blur-commit fire (submitting the task instead of opening the picker).
    setTimeout(() => this.setState({ pickerOpen: true }), 0)
  }

  closePicker = () => {
    this.setState({ pickerOpen: false })
    if (this.titleRef.current) this.titleRef.current.focus()
    this.handleBlur()
  }

  handleClientSearch = (event, { searchQuery }) => this.debouncedSearch(searchQuery || '')

  handleClientSelect = (event, { value }) => {
    const picked = this.clientOptions().find((option) => option.value === value)
    this.setState({ pendingClient: picked ? { id: picked.value, name: picked.text } : null })
  }

  clearClient = (event) => {
    event.stopPropagation() // don't let the chip's onClick re-open the picker
    this.setState({ pendingClient: null, pickerOpen: false })
  }

  // Trailing control: subtle affordance → open search dropdown → label chip.
  renderClientControl() {
    const { pendingClient, pickerOpen } = this.state

    if (pickerOpen) {
      return (
        <Dropdown
          selection
          search
          defaultOpen
          selectOnBlur={false}
          value={pendingClient ? pendingClient.id : ''}
          options={this.clientOptions()}
          onClose={this.closePicker}
          onBlur={this.closePicker}
          onChange={this.handleClientSelect}
          onSearchChange={this.handleClientSearch}
          placeholder={I18n.t('components.todo_list.client_search_placeholder')}
        />
      )
    }

    if (pendingClient) {
      return (
        <Label size='tiny' className='todo-client todo-client-pending' onClick={this.openPicker}>
          <Icon name='user' />
          {pendingClient.name}
          <Icon name='delete' onClick={this.clearClient} />
        </Label>
      )
    }

    return (
      <Icon
        name='user plus'
        link
        className='todo-link-affordance'
        title={I18n.t('components.todo_list.link_client')}
        onClick={this.openPicker}
      />
    )
  }

  render() {
    const { todos, toggleTodo } = this.props
    const { newTitle } = this.state
    return (
      <List relaxed verticalAlign='middle'>
        {todos.map((todo) => (
          <List.Item key={todo.id} className={`todo-row${todo.completedAt ? ' todo-done-row' : ''}`}>
            <Checkbox
              className={todo.completedAt ? 'todo-done' : undefined}
              checked={!!todo.completedAt}
              onChange={() => toggleTodo(todo.id, todo.completedAt ? null : moment().toISOString())}
              label={todo.title}
            />
            {todo.clientName && (
              <Label
                size='tiny'
                className='todo-client'
                as={Link}
                to={entityRoute('dossier', false, { id: todo.dossierId })}
              >
                <Icon name='user' />
                {todo.clientName}
              </Label>
            )}
          </List.Item>
        ))}
        <List.Item>
          <div className='todo-add-row'>
            <Input
              ref={this.titleRef}
              fluid
              transparent
              value={newTitle}
              placeholder={I18n.t('components.todo_list.placeholder')}
              onChange={this.handleChange}
              onKeyDown={this.handleKeyDown}
              onBlur={this.handleBlur}
            />
            {this.renderClientControl()}
          </div>
        </List.Item>
      </List>
    )
  }
}

const mapStateToProps = (state) => ({
  todos: getTodos(state),
  dossiers: getDossiers(CLIENT_PICKER_WINDOW)(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchTodos: () => dispatch(fetchEntities('todos', { name: 'compass', page: 1 })),
  fetchClients: (search) => dispatch(fetchEntities('dossiers', { name: CLIENT_PICKER_WINDOW, filter: { search }, page: 1 })),
  addTodo: (title, client) => {
    const tempId = dispatch(createEntity('todo', { title, dossierId: client ? client.id : null }))
    dispatch(upsertEntity('todo', tempId)).then((realId) => {
      if (realId) dispatch(deleteEntity('todo', tempId, false))
    })
  },
  toggleTodo: (id, completedAt) => {
    dispatch(updateEntity('todo', { id, completedAt }))
    dispatch(upsertEntity('todo', id))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(TodoList)
